import { useState, useEffect } from 'react';
import {
  Modal,
  Button,
  Table,
  notification,
  Space,
  Typography,
  Image,
} from 'antd';
import { useDropzone } from 'react-dropzone';
import { DeleteOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { setCookiesLocalStorage } from '@/services/xiaohongshu';

const { Text } = Typography;

interface CookieType {
  name: string;
  value: string;
  domain?: string;
  path?: string;
  secure?: boolean;
  httpOnly?: boolean;
  expires?: string;
}

interface LocalStorageType {
  name: string;
  value: string;
  domain?: string;
}

interface DataType {
  key: string;
  name: string;
  value: string;
  type: 'cookie' | 'localStorage'; // 添加类型标识
  domain?: string; // 添加域名信息
  expires?: string; // 添加过期时间
}

const CookiesUploadModal = ({
  visible,
  name,
  onCancel,
}: {
  visible: boolean;
  name: string;
  onCancel: () => void;
}) => {
  const [cookies, setCookies] = useState<CookieType[]>([]);
  const [localStorage, setLocalStorage] = useState<LocalStorageType[]>([]);
  const [data, setData] = useState<DataType[]>([]);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  // 当 cookies 或 localStorage 变化时，更新展示数据
  useEffect(() => {
    const cookieData: DataType[] = cookies.map((cookie) => ({
      key: `cookie_${cookie.name}`,
      name: cookie.name,
      value:
        typeof cookie.value === 'object'
          ? JSON.stringify(cookie.value)
          : String(cookie.value),
      type: 'cookie' as const,
      domain: cookie.domain,
      expires: cookie.expires,
    }));

    const localStorageData: DataType[] = localStorage.map((item) => ({
      key: `localStorage_${item.name}`,
      name: item.name,
      value:
        typeof item.value === 'object'
          ? JSON.stringify(item.value)
          : String(item.value),
      type: 'localStorage' as const,
      domain: item.domain,
      expires: undefined, // localStorage 没有过期时间
    }));

    setData([...cookieData, ...localStorageData]);
  }, [cookies, localStorage]);

  // 检查必需字段
  useEffect(() => {
    const requiredFields = [
      'a1',
      'b1',
      'webId',
      'web_session',
      'galaxy_creator_session_id',
      'galaxy.creator.beaker.session.id',
    ];
    const allFieldNames = [
      ...cookies.map((c) => c.name),
      ...localStorage.map((l) => l.name),
    ];
    const missing = requiredFields.filter(
      (field) => !allFieldNames.includes(field),
    );
    setMissingFields(missing);
  }, [cookies, localStorage]);

  const handleFinish = async () => {
    if (missingFields.length > 0) {
      notification.error({
        message: '缺少必需字段',
        description: `请确保包含以下字段：${missingFields.join(', ')}`,
        duration: 5,
      });
      return;
    }

    const result = await setCookiesLocalStorage({
      name,
      cookies,
      localStorage,
    });
    onCancel();
  };

  // 清空所有数据
  const handleClearAll = () => {
    setCookies([]);
    setLocalStorage([]);
    notification.success({
      message: '已清空',
      description: '所有数据已清空。',
    });
  };

  // 清空 Cookies
  const handleClearCookies = () => {
    setCookies([]);
    notification.success({
      message: '已清空 Cookies',
      description: 'Cookies 数据已清空。',
    });
  };

  // 清空 LocalStorage
  const handleClearLocalStorage = () => {
    setLocalStorage([]);
    notification.success({
      message: '已清空 LocalStorage',
      description: 'LocalStorage 数据已清空。',
    });
  };

  // 计算统计信息
  const getStats = () => {
    const cookieCount = data.filter((item) => item.type === 'cookie').length;
    const localStorageCount = data.filter(
      (item) => item.type === 'localStorage',
    ).length;
    const domains = [
      ...new Set(data.map((item) => item.domain).filter(Boolean)),
    ];

    return { cookieCount, localStorageCount, domains, total: data.length };
  };

  const stats = getStats();

  // 处理文件上传
  const handleFileDrop = (acceptedFiles: File[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      notification.error({
        message: '无效文件',
        description: '请上传有效的 JSON 文件。',
      });
      return;
    }

    // 处理多个文件
    acceptedFiles.forEach((file, fileIndex) => {
      processFile(file, fileIndex);
    });
  };

  // 处理单个文件
  const processFile = (file: File, fileIndex: number) => {
    // 解析文件名，提取类型和域名
    const fileName = file.name;
    const parseFileName = (fileName: string) => {
      // 匹配 cookies_[domain].json 或 localStorage_[domain].json
      const cookiesMatch = fileName.match(/^cookies_(.+)\.json$/i);
      const localStorageMatch = fileName.match(/^localStorage_(.+)\.json$/i);

      if (cookiesMatch) {
        return { type: 'cookies' as const, domain: cookiesMatch[1] };
      } else if (localStorageMatch) {
        return { type: 'localStorage' as const, domain: localStorageMatch[1] };
      }

      // 如果文件名不符合格式，尝试根据内容推断
      return { type: 'unknown' as const, domain: 'unknown' };
    };

    const fileInfo = parseFileName(fileName);

    const reader = new FileReader();
    reader.onload = () => {
      try {
        const jsonData = JSON.parse(reader.result as string);

        if (!jsonData || typeof jsonData !== 'object') {
          notification.error({
            message: `文件 ${file.name} 无效数据`,
            description: '上传的文件不包含有效的JSON对象。',
          });
          return;
        }

        let newCookies: CookieType[] = [];
        let newLocalStorage: LocalStorageType[] = [];

        if (fileInfo.type === 'cookies') {
          // 处理cookies数据
          if (Array.isArray(jsonData)) {
            // 如果是数组格式的cookies
            newCookies = jsonData.map((cookie, index) => ({
              name: cookie.name || `cookie_${index}`,
              value: String(cookie.value || ''),
              domain: cookie.domain || fileInfo.domain,
              path: cookie.path,
              secure: cookie.secure,
              httpOnly: cookie.httpOnly,
              expires: cookie.expires,
            }));
          } else {
            // 如果是对象格式的cookies，key是name，value是包含cookie属性的对象
            newCookies = Object.entries(jsonData).map(
              ([name, cookieObj]: [string, any]) => ({
                name,
                value: String(cookieObj.value || ''), // 提取对象中的value属性
                domain: cookieObj.domain || fileInfo.domain,
                path: cookieObj.path,
                secure: cookieObj.secure,
                httpOnly: cookieObj.httpOnly,
                expires: cookieObj.expires || cookieObj.expirationDate,
              }),
            );
          }
        } else if (fileInfo.type === 'localStorage') {
          // 处理localStorage数据
          newLocalStorage = Object.entries(jsonData).map(([name, value]) => ({
            name,
            value:
              typeof value === 'object' ? JSON.stringify(value) : String(value),
            domain: fileInfo.domain,
          }));
        } else {
          // 如果文件名格式不明确，尝试自动判断
          if (Array.isArray(jsonData)) {
            // 可能是cookies数组
            newCookies = jsonData.map((item, index) => ({
              name: item.name || `item_${index}`,
              value: String(item.value || item),
              domain: 'auto-detected',
            }));
          } else {
            // 检查对象的值是否像cookie对象（包含value属性）
            const firstEntry = Object.entries(jsonData)[0];
            if (
              firstEntry &&
              firstEntry[1] &&
              typeof firstEntry[1] === 'object' &&
              'value' in firstEntry[1]
            ) {
              // 当作cookies处理（对象格式）
              newCookies = Object.entries(jsonData).map(
                ([name, cookieObj]: [string, any]) => ({
                  name,
                  value: String(cookieObj.value || ''),
                  domain: cookieObj.domain || 'auto-detected',
                  path: cookieObj.path,
                  secure: cookieObj.secure,
                  httpOnly: cookieObj.httpOnly,
                  expires: cookieObj.expires || cookieObj.expirationDate,
                }),
              );
            } else {
              // 当作localStorage处理
              newLocalStorage = Object.entries(jsonData).map(
                ([name, value]) => ({
                  name,
                  value:
                    typeof value === 'object'
                      ? JSON.stringify(value)
                      : String(value),
                  domain: 'auto-detected',
                }),
              );
            }
          }
        }

        if (newCookies.length === 0 && newLocalStorage.length === 0) {
          notification.error({
            message: `文件 ${file.name} 无效数据`,
            description: '未能从文件中解析出有效的cookies或localStorage数据。',
          });
          return;
        }

        // 更新对应的状态
        if (newCookies.length > 0) {
          setCookies((prev) => {
            const map = new Map<string, CookieType>(
              prev.map((cookie) => [cookie.name, cookie]),
            );
            newCookies.forEach((cookie) => {
              map.set(cookie.name, cookie);
            });
            return Array.from(map.values());
          });
        }

        if (newLocalStorage.length > 0) {
          setLocalStorage((prev) => {
            const map = new Map<string, LocalStorageType>(
              prev.map((item) => [item.name, item]),
            );
            newLocalStorage.forEach((item) => {
              map.set(item.name, item);
            });
            return Array.from(map.values());
          });
        }

        const totalItems = newCookies.length + newLocalStorage.length;
        notification.success({
          message: `文件 ${file.name} 上传成功`,
          description: `成功解析 ${totalItems} 个项目${
            fileInfo.domain !== 'unknown' ? ` (域名: ${fileInfo.domain})` : ''
          }。${newCookies.length > 0 ? ` Cookies: ${newCookies.length}` : ''}${
            newLocalStorage.length > 0
              ? ` LocalStorage: ${newLocalStorage.length}`
              : ''
          }`,
        });
      } catch (e) {
        notification.error({
          message: `文件 ${file.name} 解析失败`,
          description: '无法解析该文件，请确保上传的是正确的 JSON 格式。',
        });
      }
    };
    reader.readAsText(file);
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { 'application/json': ['.json'] },
    onDrop: handleFileDrop,
    multiple: true, // 支持多文件上传
  });

  // Table 列配置
  const columns = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 120,
      render: (type: 'cookie' | 'localStorage') => (
        <span
          style={{
            color: type === 'cookie' ? '#1890ff' : '#52c41a',
            fontWeight: 'bold',
          }}
        >
          {type === 'cookie' ? 'Cookie' : 'LocalStorage'}
        </span>
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (text: string) => (
        <div
          style={{
            wordBreak: 'break-word',
            whiteSpace: 'normal',
            maxWidth: '300px',
            overflowWrap: 'break-word',
          }}
        >
          {text}
        </div>
      ),
    },
    {
      title: '过期时间',
      dataIndex: 'expires',
      key: 'expires',
      width: 180,
      render: (expires: string | undefined, record: DataType) => {
        if (record.type === 'localStorage') {
          return <span style={{ color: '#999' }}>-</span>;
        }

        if (!expires) {
          return <span style={{ color: '#999' }}>-</span>;
        }

        // 如果是时间戳（数字），转换为日期
        if (/^\d+(\.\d+)?$/.test(expires)) {
          const timestamp = parseFloat(expires) * 1000; // 转换为毫秒
          const date = new Date(timestamp);
          if (!isNaN(date.getTime())) {
            return (
              <span style={{ fontSize: '12px' }}>
                {date.toLocaleString('zh-CN')}
              </span>
            );
          }
        }

        // 如果已经是日期字符串，直接显示
        return <span style={{ fontSize: '12px' }}>{expires}</span>;
      },
    },
  ];

  // 导出 Cookies 数据
  const exportCookies = () => {
    if (cookies.length === 0) {
      notification.warning({
        message: '无数据可导出',
        description: '当前没有 Cookies 数据。',
      });
      return;
    }

    const dataStr = JSON.stringify(cookies, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `cookies_export_${
      new Date().toISOString().split('T')[0]
    }.json`;
    link.click();
    URL.revokeObjectURL(url);

    notification.success({
      message: '导出成功',
      description: `已导出 ${cookies.length} 个 Cookies。`,
    });
  };

  // 导出 LocalStorage 数据
  const exportLocalStorage = () => {
    if (localStorage.length === 0) {
      notification.warning({
        message: '无数据可导出',
        description: '当前没有 LocalStorage 数据。',
      });
      return;
    }

    const dataObj = localStorage.reduce((acc, item) => {
      acc[item.name] = item.value;
      return acc;
    }, {} as Record<string, string>);

    const dataStr = JSON.stringify(dataObj, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `localStorage_export_${
      new Date().toISOString().split('T')[0]
    }.json`;
    link.click();
    URL.revokeObjectURL(url);

    notification.success({
      message: '导出成功',
      description: `已导出 ${localStorage.length} 个 LocalStorage 项目。`,
    });
  };

  return (
    <Modal
      title="上传 localStorage 或 Cookies 数据"
      visible={visible}
      onCancel={onCancel}
      footer={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Space size="middle">
            <Button
              icon={<DeleteOutlined />}
              onClick={handleClearCookies}
              disabled={cookies.length === 0}
              size="small"
            >
              清空 Cookies ({cookies.length})
            </Button>
            <Button
              icon={<DeleteOutlined />}
              onClick={handleClearLocalStorage}
              disabled={localStorage.length === 0}
              size="small"
            >
              清空 LocalStorage ({localStorage.length})
            </Button>
            <Button
              icon={<DeleteOutlined />}
              onClick={handleClearAll}
              disabled={data.length === 0}
              danger
            >
              全部清空
            </Button>
          </Space>
          <Button
            type="primary"
            onClick={handleFinish}
            disabled={missingFields.length > 0}
          >
            确定
          </Button>
        </div>
      }
      width={800}
      bodyStyle={{
        maxHeight: '80vh',
        overflow: 'hidden',
        padding: '0 20px', // 统一左右 padding
        display: 'flex',
        flexDirection: 'column',
      }} // 设置最大高度，隐藏溢出，使用 flex 布局
    >
      {/* 下载插件链接 */}
      <div
        style={{
          textAlign: 'center',
          marginBottom: 16,
          padding: '12px',
          backgroundColor: '#f6ffed',
          border: '1px solid #b7eb8f',
          borderRadius: '6px',
          flexShrink: 0,
        }}
      >
        <Text style={{ marginRight: 8 }}>需要提取浏览器数据？</Text>
        <a
          href="https://bb8-robot.oss-rg-china-mainland.aliyuncs.com/addons/full-cookie-viewer.zip"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#52c41a',
            fontWeight: 'bold',
            textDecoration: 'underline',
            marginRight: 12,
          }}
        >
          点我下载插件
        </a>
        <Button
          type="link"
          icon={<QuestionCircleOutlined />}
          onClick={() => setTutorialVisible(true)}
          style={{
            padding: 0,
            height: 'auto',
            color: '#1890ff',
            fontSize: '14px',
          }}
        >
          查看教程
        </Button>
        <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
          安装浏览器插件后可导出当前网站的 Cookies 和 LocalStorage 数据
        </div>
      </div>

      <div
        {...getRootProps()}
        style={{
          border: '2px dashed #1890ff',
          padding: 20,
          textAlign: 'center',
          flexShrink: 0,
          marginBottom: 20,
        }}
      >
        <input {...getInputProps()} />
        <p>将 Cookies 或 LocalStorage 的 JSON 文件拖拽到这里</p>
        <p style={{ fontWeight: 'bold', color: '#1890ff' }}>
          支持多个文件同时上传
        </p>
        <p>
          支持文件名格式：<code>cookies_[domain].json</code> 或{' '}
          <code>localStorage_[domain].json</code>
        </p>
        <p style={{ fontSize: '12px', color: '#666' }}>
          例如: cookies_example.com.json, localStorage_github.com.json
        </p>
      </div>

      {data.length > 0 && (
        <div style={{ marginBottom: 16, flexShrink: 0 }}>
          <Space
            wrap
            style={{ width: '100%', justifyContent: 'space-between' }}
          >
            <Space wrap>
              <Text>
                <strong>统计:</strong> 总计 {stats.total} 项
              </Text>
              {stats.cookieCount > 0 && (
                <Text type="secondary">Cookies: {stats.cookieCount}</Text>
              )}
              {stats.localStorageCount > 0 && (
                <Text type="secondary">
                  LocalStorage: {stats.localStorageCount}
                </Text>
              )}
              {stats.domains.length > 0 && (
                <Text type="secondary">域名: {stats.domains.join(', ')}</Text>
              )}
            </Space>
            <Space>
              <Button
                size="small"
                onClick={exportCookies}
                disabled={cookies.length === 0}
              >
                导出 Cookies
              </Button>
              <Button
                size="small"
                onClick={exportLocalStorage}
                disabled={localStorage.length === 0}
              >
                导出 LocalStorage
              </Button>
            </Space>
          </Space>
        </div>
      )}

      {/* 缺失字段提示 */}
      {missingFields.length > 0 && (
        <div
          style={{
            marginBottom: 16,
            padding: '12px',
            backgroundColor: '#fff2f0',
            border: '1px solid #ffccc7',
            borderRadius: '6px',
            flexShrink: 0,
          }}
        >
          <Text type="danger" style={{ fontWeight: 'bold' }}>
            ⚠️ 缺少必需字段：
          </Text>
          <Text type="danger" style={{ marginLeft: 8 }}>
            {missingFields.join(', ')}
          </Text>
          <div style={{ fontSize: '12px', color: '#999', marginTop: 4 }}>
            请确保上传的文件包含这些字段，否则无法提交
          </div>
        </div>
      )}

      <div style={{ flex: '1 1 auto', overflowY: 'auto' }}>
        <Table
          columns={columns}
          dataSource={data}
          rowKey="key"
          pagination={false}
          style={{ maxHeight: '60vh' }} // 限制表格的最大高度
        />
      </div>

      {/* 教程图片模态框 */}
      <Modal
        title="插件使用教程"
        visible={tutorialVisible}
        onCancel={() => setTutorialVisible(false)}
        footer={null}
        width={800}
        centered
      >
        <div style={{ textAlign: 'center' }}>
          <Image
            src="https://bb8-robot.oss-rg-china-mainland.aliyuncs.com/fonts/screenshot-20250702-165240.png"
            alt="插件使用教程"
            style={{ maxWidth: '100%' }}
            preview={{
              src: 'https://bb8-robot.oss-rg-china-mainland.aliyuncs.com/fonts/screenshot-20250702-165240.png',
            }}
          />
        </div>
      </Modal>
    </Modal>
  );
};

export default CookiesUploadModal;
